import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Chip,
  Container,
  Paper,
  Divider
} from '@mui/material';
import {
  Person as PersonIcon,
  LocationOn as LocationIcon,
  DirectionsCar as CarIcon,
  Assignment as AssignmentIcon,
  Update as UpdateIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';

interface DriverInfo {
  id: number;
  name: string;
  email: string;
  phone: string;
  license_number: string;
  vehicle_type: string;
  vehicle_number: string;
  status: 'available' | 'busy' | 'offline';
  current_location: string;
  rating: number;
  total_deliveries: number;
  successful_deliveries: number;
  created_at: string;
  updated_at: string;
}

interface Shipment {
  id: number;
  tracking_number: string;
  status: string;
  pickup_address: string;
  delivery_address: string;
  weight: number;
  created_at: string;
}

const statusColors = {
  available: 'success',
  busy: 'warning',
  offline: 'error'
} as const;

const statusLabels = {
  available: 'available',
  busy: 'busy', 
  offline: 'offline'
} as const;

export default function DriverStatus() {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useLanguage();
  
  const [driverInfo, setDriverInfo] = useState<DriverInfo | null>(null);
  const [myShipments, setMyShipments] = useState<Shipment[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [newStatus, setNewStatus] = useState<'available' | 'busy' | 'offline'>('available');
  const [newLocation, setNewLocation] = useState('');

  // Fetch driver status and shipments
  const fetchDriverData = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('No access token found');
      }

      // Fetch driver status
      const statusResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080/api'}/drivers/status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!statusResponse.ok) {
        throw new Error('Failed to fetch driver status');
      }

      const driverData = await statusResponse.json();
      setDriverInfo(driverData);
      setNewStatus(driverData.status);
      setNewLocation(driverData.current_location || '');

      // Fetch assigned shipments
      const shipmentsResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080/api'}/drivers/my-shipments`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (shipmentsResponse.ok) {
        const shipmentsData = await shipmentsResponse.json();
        setMyShipments(Array.isArray(shipmentsData) ? shipmentsData : []);
      }
    } catch (error) {
      console.error('Error fetching driver data:', error);
      enqueueSnackbar(t('errors.loadData'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Update driver status
  const updateDriverStatus = async () => {
    if (!driverInfo) return;

    setUpdating(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080/api'}/drivers/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: newStatus,
          current_location: newLocation
        })
      });

      if (!response.ok) {
        throw new Error(t('drivers.statusUpdate.error'));
      }

      const updatedDriver = await response.json();
      setDriverInfo(updatedDriver);
      enqueueSnackbar(t('drivers.statusUpdate.success'), { variant: 'success' });
    } catch (error) {
      console.error('Error updating status:', error);
      enqueueSnackbar(t('drivers.statusUpdate.error'), { variant: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  useEffect(() => {
    fetchDriverData();
  }, []);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!driverInfo) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          Failed to load driver information. Please try refreshing the page.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Driver Status Dashboard
      </Typography>
      
      <Grid container spacing={3}>
        {/* Driver Information Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Driver Information</Typography>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">Name</Typography>
                  <Typography variant="body1">{driverInfo.name}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">Email</Typography>
                  <Typography variant="body1">{driverInfo.email}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Phone</Typography>
                  <Typography variant="body1">{driverInfo.phone}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">License</Typography>
                  <Typography variant="body1">{driverInfo.license_number}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Vehicle Type</Typography>
                  <Typography variant="body1">{driverInfo.vehicle_type}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Vehicle Number</Typography>
                  <Typography variant="body1">{driverInfo.vehicle_number}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Rating</Typography>
                  <Typography variant="body1">{driverInfo.rating}/5.0</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Total Deliveries</Typography>
                  <Typography variant="body1">{driverInfo.total_deliveries}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Status Update Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <UpdateIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Update Status</Typography>
              </Box>
              
              <Box mb={2}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Current Status
                </Typography>
                <Chip 
                  label={statusLabels[driverInfo.status]}
                  color={statusColors[driverInfo.status]}
                  variant="outlined"
                />
              </Box>

              <Box mb={2}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Current Location
                </Typography>
                <Box display="flex" alignItems="center">
                  <LocationIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 16 }} />
                  <Typography variant="body1">
                    {driverInfo.current_location || 'Not specified'}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>New Status</InputLabel>
                    <Select
                      value={newStatus}
                      label="New Status"
                      onChange={(e) => setNewStatus(e.target.value as 'available' | 'busy' | 'offline')}
                    >
                      <MenuItem value="available">Available</MenuItem>
                      <MenuItem value="busy">Busy</MenuItem>
                      <MenuItem value="offline">Offline</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Current Location"
                    value={newLocation}
                    onChange={(e) => setNewLocation(e.target.value)}
                    placeholder="Enter your current location"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    onClick={updateDriverStatus}
                    disabled={updating}
                    fullWidth
                    startIcon={updating ? <CircularProgress size={20} /> : <UpdateIcon />}
                  >
                    {updating ? 'Updating...' : 'Update Status'}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Assigned Shipments */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <AssignmentIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">My Assigned Shipments</Typography>
              </Box>
              
              {myShipments.length === 0 ? (
                <Alert severity="info">
                  No shipments currently assigned to you.
                </Alert>
              ) : (
                <Grid container spacing={2}>
                  {myShipments.map((shipment) => (
                    <Grid item xs={12} md={6} key={shipment.id}>
                      <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="subtitle1" gutterBottom>
                          {shipment.tracking_number}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" component="span">
                          Status: <Chip label={shipment.status} size="small" />
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          From: {shipment.pickup_address}
                        </Typography>
                        <Typography variant="body2">
                          To: {shipment.delivery_address}
                        </Typography>
                        <Typography variant="body2">
                          Weight: {shipment.weight} kg
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}
